import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Alert, Button, Card, Checkbox, Col, DatePicker, Empty, Icon, Input, Row, Select, Slider, Spin, Statistic, Table, Tag, message
} from 'antd';
import L from 'leaflet';
import { Circle, CircleMarker, Map as LeafletMap, Marker, Polyline, TileLayer, Tooltip as LeafletTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import moment from 'moment';

import { fetchBeaconData } from './utils/passiveAssetService';
import { normalizeBeaconList } from './utils/passiveAssetNormalizers';
import { loadAssetSensorIndex } from './utils/passiveAssetResolver';
import { buildDetectionIntervals, fetchReaderTrack } from './utils/passiveAssetMovement';
import { rssiToDistance, rssiConfidenceBand, CONFIDENCE_COLORS } from './utils/passiveAssetRssi';
import usePlayback from './utils/usePlayback';
import { useGeoFences } from './utils/geoFenceService';
import {
  assetLabel, formatTime, formatHm, makePlaybackPinIcon, makeNoGpsPinIcon,
  ESRI_TILE_URL, ESRI_TILE_ATTRIBUTION, renderGeoFencePolygons
} from './utils/passiveAssetUi';

const { RangePicker } = DatePicker;
const { Option } = Select;

const DEFAULT_RANGE = () => [moment().subtract(24, 'hours'), moment()];

// Distinct colors per FOUND→LOST window (reader movement track).
const TRACK_COLORS = ['#1890ff', '#13c2c2', '#722ed1', '#fa8c16', '#eb2f96', '#2f54eb', '#a0d911', '#fa541c'];

// A FOUND→LOST window only gets reader GPS fetched/plotted if it lasted longer
// than this AND the reader actually moved more than this distance during it.
// Shorter = detected while passing; no movement = detected in a parking lot.
const MIN_FOUND_DURATION_MS = 2 * 60 * 1000; // 2 minutes
const MIN_READER_MOVE_M = 30;                // metres

const haversineM = (a, b) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad((b.lat || 0) - (a.lat || 0));
  const dLng = toRad((b.lng || 0) - (a.lng || 0));
  const lat1 = toRad(a.lat || 0);
  const lat2 = toRad(b.lat || 0);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

const pathDistanceM = (pts) => {
  let d = 0;
  for (let i = 1; i < pts.length; i += 1) d += haversineM(pts[i - 1], pts[i]);
  return d;
};

const validCoord = (r) =>
  Number.isFinite(Number(r.latitude)) && Number.isFinite(Number(r.longitude)) &&
  !(Math.abs(Number(r.latitude)) < 1e-6 && Math.abs(Number(r.longitude)) < 1e-6);

function BleAssetTracker() {
  const [range, setRange] = useState(DEFAULT_RANGE());
  const [macInput, setMacInput] = useState('');
  const [reads, setReads] = useState([]);
  const [readerTracks, setReaderTracks] = useState([]); // [{readerImei, intervals, points}]
  const [loading, setLoading] = useState(false);
  const [trackLoading, setTrackLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [queriedMac, setQueriedMac] = useState('');

  const [assetIndex, setAssetIndex] = useState(null);
  const [showCircles, setShowCircles] = useState(true);
  const [focusedDetId, setFocusedDetId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapRef = useRef(null);
  const tableBodyRef = useRef(null);
  const runRef = useRef(0);

  const fromMs = range && range[0] ? range[0].valueOf() : null;
  const toMs = range && range[1] ? range[1].valueOf() : null;

  const playbackPinIcon = useMemo(() => makePlaybackPinIcon(), []);
  const noGpsPinIcon = useMemo(() => makeNoGpsPinIcon(), []);
  const { geoFences } = useGeoFences();

  useEffect(() => {
    let cancelled = false;
    loadAssetSensorIndex().then((idx) => { if (!cancelled) setAssetIndex(idx); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const resolveTag = useCallback((id) => (assetIndex ? assetIndex.resolveTag(id) : null), [assetIndex]);
  const resolveReader = useCallback((id) => (assetIndex ? assetIndex.resolveReader(id) : null), [assetIndex]);

  // Leaflet must recompute its size after the container resizes (fullscreen).
  useEffect(() => {
    const t = setTimeout(() => {
      const m = mapRef.current && (mapRef.current.leafletElement || mapRef.current);
      if (m && typeof m.invalidateSize === 'function') m.invalidateSize();
    }, 250);
    return () => clearTimeout(t);
  }, [isFullscreen]);

  // Esc exits fullscreen.
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  const load = useCallback(async () => {
    if (!fromMs || !toMs || fromMs >= toMs) { message.warning('Pick a valid date range.'); return; }
    const mac = (macInput || '').trim();
    if (!mac) { message.warning('Enter a Beacon MAC.'); return; }

    const runId = runRef.current + 1;
    runRef.current = runId;
    setLoading(true);
    setError(null);
    setNotice(null);
    setReads([]);
    setReaderTracks([]);
    setTrackLoading(false);
    setQueriedMac(mac);

    try {
      const res = await fetchBeaconData({ from: fromMs, to: toMs, mac });
      if (runRef.current !== runId) return; // superseded by a newer query
      if (res && res.success === false) { setError(res.error || 'Failed to fetch beacon data'); setLoading(false); return; }
      const normalized = normalizeBeaconList(res.data === undefined ? res : res.data);
      setReads(normalized);
      // Detections are ready — the map + detection-point playback are usable now.
      setLoading(false);
      if (normalized.length === 0) { setNotice('No FOUND/LOST events for this tag in the selected period.'); return; }

      // Pull each detecting reader's GPS IN THE BACKGROUND so the reader-movement
      // overlay/playback appears when ready without blocking the detection view.
      setTrackLoading(true);
      const { intervals } = buildDetectionIntervals(normalized, toMs);
      const byReader = new Map();
      intervals.forEach((itv) => {
        if (!byReader.has(itv.readerImei)) byReader.set(itv.readerImei, { readerImei: itv.readerImei, intervals: [], spanStart: itv.startTs, spanEnd: itv.endTs });
        const g = byReader.get(itv.readerImei);
        g.intervals.push(itv);
        g.spanStart = Math.min(g.spanStart, itv.startTs);
        g.spanEnd = Math.max(g.spanEnd, itv.endTs);
      });

      const results = [];
      let unlinked = 0;
      for (const rd of Array.from(byReader.values())) {
        // Only fetch reader GPS if at least one of its windows lasted > 2 min;
        // shorter windows are pass-bys and don't warrant a movement track.
        const hasLong = rd.intervals.some((itv) => (itv.endTs - itv.startTs) > MIN_FOUND_DURATION_MS);
        let points = [];
        if (hasLong) {
          const info = resolveReader(rd.readerImei);
          const assetId = info ? info.assetId : null;
          if (assetId) {
            // eslint-disable-next-line no-await-in-loop
            const tr = await fetchReaderTrack({ readerImei: rd.readerImei, assetId, startTs: rd.spanStart, endTs: rd.spanEnd });
            if (runRef.current !== runId) return; // superseded
            points = tr.points || [];
          } else {
            unlinked += 1;
          }
        }
        results.push({ readerImei: rd.readerImei, intervals: rd.intervals, points });
      }
      if (runRef.current !== runId) return;
      setReaderTracks(results);
      setTrackLoading(false);
      if (unlinked > 0) setNotice(`${unlinked} reader(s) not linked to an asset — their GPS movement can't be drawn.`);
    } catch (err) {
      if (runRef.current !== runId) return;
      setError(err.message || 'Failed to load');
      setLoading(false);
      setTrackLoading(false);
    }
  }, [fromMs, toMs, macInput, resolveReader]);

  // RSSI-radius circles for every detection with a location.
  const detectionCircles = useMemo(() => reads.filter(validCoord).map((r, i) => {
    const distance = rssiToDistance(r.rssi);
    return {
      id: `dc-${i}`,
      key: `${r.eventTime}|${r.readerImei}`, // matches a clicked table row
      lat: Number(r.latitude),
      lng: Number(r.longitude),
      rssi: r.rssi,
      distance,
      confidence: rssiConfidenceBand(distance),
      status: String(r.status || '').toUpperCase(),
      readerImei: r.readerImei,
      ts: r.eventTime
    };
  }), [reads]);

  // When a table row is focused, show only that detection; otherwise honour the
  // "show signal circles" toggle. Dots (FOUND/LOST markers) are smaller and stay
  // visible except when focusing a single point.
  const visibleCircles = useMemo(() => {
    if (focusedDetId) return detectionCircles.filter((c) => c.key === focusedDetId);
    return showCircles ? detectionCircles : [];
  }, [detectionCircles, focusedDetId, showCircles]);
  const visibleDots = useMemo(
    () => (focusedDetId ? detectionCircles.filter((c) => c.key === focusedDetId) : detectionCircles),
    [detectionCircles, focusedDetId]
  );

  // One reader-movement polyline per QUALIFYING FOUND→LOST window. The window
  // runs from FOUND to LOST (or to the end of the period if still in range).
  // A window only qualifies — and we only plot the reader GPS — when BOTH:
  //   (a) it lasted > 2 minutes, and
  //   (b) the reader's GPS actually moved > 30 m during it.
  // Otherwise it was a pass-by or a parking-lot detection: show only the tag.
  const intervalTracks = useMemo(() => {
    const out = [];
    readerTracks.forEach((t) => {
      (t.intervals || []).forEach((itv) => {
        const longEnough = (itv.endTs - itv.startTs) > MIN_FOUND_DURATION_MS;
        if (!longEnough) return;
        const pts = (t.points || []).filter((p) => p.ts >= itv.startTs && p.ts <= itv.endTs);
        if (pts.length < 2) return;
        const moved = pathDistanceM(pts);
        if (moved < MIN_READER_MOVE_M) return; // reader stationary → parking lot
        out.push({ readerImei: t.readerImei, startTs: itv.startTs, endTs: itv.endTs, open: itv.open, points: pts, distanceM: moved });
      });
    });
    out.sort((a, b) => a.startTs - b.startTs);
    return out;
  }, [readerTracks]);

  // Reader-movement points across all qualifying FOUND→LOST windows.
  const movementPoints = useMemo(() => {
    const pts = [];
    intervalTracks.forEach((it, idx) => {
      it.points.forEach((p) => pts.push({
        id: `${idx}-${p.id || p.ts}`, ts: p.ts, lat: p.lat, lng: p.lng, speed: p.speed,
        noGps: p.noGps === true, readerImei: it.readerImei, segId: idx, detection: false
      }));
    });
    return pts;
  }, [intervalTracks]);

  // Fallback timeline = the detection events themselves (so you can step from
  // one detection to the next even when no reader GPS movement is available).
  const detectionPoints = useMemo(
    () => detectionCircles.slice().sort((a, b) => a.ts - b.ts).map((c, i) => ({
      id: `det-${c.id || i}`, ts: c.ts, lat: c.lat, lng: c.lng, readerImei: c.readerImei,
      status: c.status, rssi: c.rssi, segId: 'det', detection: true
    })),
    [detectionCircles]
  );

  // Use reader movement when we have it; otherwise step through detections.
  const usingDetectionTimeline = movementPoints.length === 0 && detectionPoints.length > 0;
  const playbackPoints = movementPoints.length > 0 ? movementPoints : detectionPoints;

  const pb = usePlayback(playbackPoints.length);
  const activePoint = playbackPoints.length ? playbackPoints[Math.max(0, Math.min(pb.index, playbackPoints.length - 1))] : null;

  // Seek the playback to the point nearest a given time.
  const seekToTs = useCallback((ts) => {
    pb.setIsPlaying(false);
    if (playbackPoints.length === 0) return;
    let best = 0; let bestDiff = Infinity;
    playbackPoints.forEach((p, i) => { const d = Math.abs(p.ts - ts); if (d < bestDiff) { bestDiff = d; best = i; } });
    pb.setIndex(best);
  }, [playbackPoints, pb]);

  const playedPositions = useMemo(() => {
    const out = [];
    for (let i = 0; i <= Math.min(pb.index, playbackPoints.length - 1); i += 1) {
      const p = playbackPoints[i];
      if (i > 0 && p.segId !== playbackPoints[i - 1].segId) break; // don't cross windows
      out.push([p.lat, p.lng]);
    }
    return out;
  }, [playbackPoints, pb.index]);

  // Fit bounds to all circles + tracks.
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current.leafletElement || mapRef.current;
    if (!map || typeof map.fitBounds !== 'function') return;
    const pts = [];
    detectionCircles.forEach((c) => pts.push([c.lat, c.lng]));
    intervalTracks.forEach((it) => it.points.forEach((p) => pts.push([p.lat, p.lng])));
    if (pts.length === 1) { map.setView(pts[0], 17); return; }
    if (pts.length >= 2) map.fitBounds(L.latLngBounds(pts), { padding: [32, 32], maxZoom: 18 });
  }, [detectionCircles, intervalTracks]);

  const assetInfo = resolveTag(queriedMac);

  const eventRows = useMemo(
    () => reads.slice().sort((a, b) => (b.eventTime || 0) - (a.eventTime || 0)),
    [reads]
  );

  const summary = useMemo(() => {
    const readers = new Set();
    let found = 0; let lost = 0;
    reads.forEach((r) => {
      if (r.readerImei && r.readerImei !== '-') readers.add(r.readerImei);
      const s = String(r.status || '').toUpperCase();
      if (s === 'FOUND') found += 1; else if (s === 'LOST') lost += 1;
    });
    return { readers: readers.size, found, lost };
  }, [reads]);

  const renderIdentity = (rawId, info) => (
    <div>
      <div style={{ fontWeight: 600 }}>{(info && (info.displayName || info.assetCode)) || rawId || '-'}</div>
      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
        {info && info.ownerCode ? `Owner ${info.ownerCode} · ` : ''}{rawId || ''}
        {info && info.viaDeviceCode ? ` · via ${info.viaDeviceCode}` : ''}
      </div>
    </div>
  );

  const eventColumns = [
    { title: 'Event Time', key: 'time', width: 170, render: (_, r) => formatTime(r.eventTime), sorter: (a, b) => (a.eventTime || 0) - (b.eventTime || 0), defaultSortOrder: 'descend' },
    { title: 'Status', key: 'status', width: 90, render: (_, r) => {
      const s = String(r.status || '').toUpperCase();
      return s === 'FOUND' ? <Tag color="green">FOUND</Tag> : s === 'LOST' ? <Tag color="red">LOST</Tag> : <Tag>{r.status || '-'}</Tag>;
    } },
    { title: 'BLE Asset', key: 'asset', render: (_, r) => renderIdentity(r.assetIdentifier, resolveTag(r.assetIdentifier)) },
    { title: 'Reader', key: 'reader', render: (_, r) => renderIdentity(r.readerImei, resolveReader(r.readerImei)) },
    { title: 'RSSI', key: 'rssi', width: 90, render: (_, r) => (r.rssi !== null && r.rssi !== undefined ? `${r.rssi} dBm` : '-') },
    { title: '~Range', key: 'range', width: 90, render: (_, r) => (r.rssi !== null && r.rssi !== undefined ? `${rssiToDistance(r.rssi).toFixed(0)} m` : '-') },
    { title: 'Zone', key: 'zone', render: (_, r) => (r.zone && r.zone !== '-' ? r.zone : '-') },
    { title: 'Lat / Lng', key: 'latlng', width: 170, render: (_, r) => (validCoord(r) ? `${Number(r.latitude).toFixed(5)}, ${Number(r.longitude).toFixed(5)}` : '-') }
  ];

  return (
    <div className="ble-asset-tracker">
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="BLE Asset Tracker — events, signal range & reader movement"
        description="Pick one BLE tag (Beacon MAC). We list every FOUND/LOST event, plot each detection as a signal-strength radius around the reader, and draw the reader's movement during each FOUND→LOST window. Locations are last-known / detected, not live."
      />

      <Card title={<span><Icon type="filter" /> Filters</span>} style={{ marginBottom: 16 }}>
        <Row gutter={12} type="flex" align="bottom">
          <Col xs={24} sm={10} md={8}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Date Range</div>
            <RangePicker style={{ width: '100%' }} showTime value={range} onChange={(v) => setRange(v || DEFAULT_RANGE())} format="YYYY-MM-DD HH:mm" />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Beacon MAC</div>
            <Input placeholder="e.g. 7CD9F410E7D6" value={macInput} onChange={(e) => setMacInput(e.target.value)} allowClear onPressEnter={load} />
          </Col>
          <Col>
            <Button type="primary" icon="search" onClick={load} loading={loading}>Track Asset</Button>
          </Col>
        </Row>
      </Card>

      {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />}
      {notice && <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={notice} />}

      {queriedMac && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="BLE Asset" value={assetLabel(queriedMac, assetInfo)} valueStyle={{ fontSize: 15 }} /></Card></Col>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="Events" value={reads.length} /></Card></Col>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="FOUND / LOST" value={`${summary.found} / ${summary.lost}`} valueStyle={{ fontSize: 16 }} /></Card></Col>
          <Col xs={12} sm={6}><Card size="small"><Statistic title="Readers" value={summary.readers} /></Card></Col>
        </Row>
      )}

      <Card
        size="small"
        style={{ marginBottom: 12 }}
        title={<span><Icon type="environment" /> Detection Map <span style={{ fontWeight: 400, fontSize: 12, color: '#888', marginLeft: 8 }}>Circle = signal-range around reader · line = reader movement (only for FOUND windows &gt; 2 min where the reader actually moved &gt; 30 m)</span></span>}
      >
        <Spin spinning={loading}>
          {playbackPoints.length > 0 && (
            <Row gutter={12} type="flex" align="middle" style={{ marginBottom: 10, rowGap: 8 }}>
              <Col>
                <Button.Group size="small">
                  <Button icon="fast-backward" onClick={pb.reset} disabled={playbackPoints.length === 0}>Reset</Button>
                  <Button icon="step-backward" onClick={pb.back} disabled={pb.index <= 0}>Back</Button>
                  <Button type={pb.isPlaying ? 'default' : 'primary'} icon={pb.isPlaying ? 'pause' : 'caret-right'} onClick={pb.toggle} disabled={playbackPoints.length <= 1}>{pb.isPlaying ? 'Pause' : 'Play'}</Button>
                  <Button icon="step-forward" onClick={pb.step} disabled={pb.index >= playbackPoints.length - 1}>Step</Button>
                </Button.Group>
              </Col>
              <Col>
                <span style={{ fontSize: 12, color: '#666', marginRight: 4 }}>Speed:</span>
                <Select size="small" value={pb.speed} onChange={pb.setSpeed} style={{ width: 80 }}>
                  {[0.5, 1, 2, 4, 8].map((s) => <Option key={s} value={s}>{s}x</Option>)}
                </Select>
              </Col>
              <Col>
                <Checkbox checked={showCircles} onChange={(e) => setShowCircles(e.target.checked)}>Signal circles</Checkbox>
              </Col>
              <Col>
                <Button size="small" icon="fullscreen" onClick={() => setIsFullscreen(true)}>Fullscreen</Button>
              </Col>
              {focusedDetId && (
                <Col>
                  <Button size="small" icon="eye" onClick={() => setFocusedDetId(null)}>Show all points</Button>
                </Col>
              )}
              {activePoint && (
                <Col flex="auto">
                  {activePoint.detection ? (
                    <span style={{ fontSize: 12, color: '#444' }}>
                      {formatTime(activePoint.ts)} · Reader <strong>{assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}</strong>
                      {activePoint.rssi != null ? ` · ${activePoint.rssi} dBm` : ''}
                      &nbsp;<Tag color={activePoint.status === 'FOUND' ? 'green' : activePoint.status === 'LOST' ? 'red' : 'default'}>{activePoint.status || 'detection'}</Tag>
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: '#444' }}>
                      Reader <strong>{assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}</strong> · {formatTime(activePoint.ts)}
                      {activePoint.noGps ? ' · ⚠ no GPS' : (activePoint.speed != null ? ` · ${Number(activePoint.speed).toFixed(0)} km/h` : '')}
                      &nbsp;<Tag color="blue">while FOUND</Tag>
                    </span>
                  )}
                </Col>
              )}
            </Row>
          )}

          {queriedMac && (
            <div style={{ marginBottom: 10, fontSize: 12, color: '#666' }}>
              {trackLoading && <span style={{ marginRight: 8 }}><Icon type="sync" spin /> loading reader GPS in background…</span>}
              {intervalTracks.length} reader-movement window(s) plotted
              {usingDetectionTimeline
                ? <span style={{ color: '#999' }}> · {trackLoading ? 'meanwhile, ' : 'no reader GPS movement — '}step/animate through the {detectionPoints.length} detection point(s)</span>
                : <span style={{ color: '#999' }}> · short pass-bys (&lt; 2 min) and stationary (parking-lot) detections show as tag circles only</span>}
            </div>
          )}

          {playbackPoints.length > 1 && (
            <div style={{ marginBottom: 10 }}>
              <Slider
                min={0}
                max={playbackPoints.length - 1}
                value={pb.index}
                onChange={(v) => { pb.setIsPlaying(false); pb.setIndex(v); }}
                tipFormatter={(v) => (playbackPoints[v] ? formatHm(playbackPoints[v].ts) : '')}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888' }}>
                <span>{formatTime(playbackPoints[0].ts)}</span>
                <span>{pb.index + 1} / {playbackPoints.length}</span>
                <span>{formatTime(playbackPoints[playbackPoints.length - 1].ts)}</span>
              </div>
            </div>
          )}

          {detectionCircles.length === 0 && intervalTracks.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={queriedMac ? 'No locatable events to map for this tag/period.' : 'Enter a Beacon MAC and click Track Asset.'} />
          ) : (
            <div style={isFullscreen
              ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, height: '100vh', width: '100vw', zIndex: 1000, background: '#fff', overflow: 'hidden' }
              : { height: 520, border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
              {isFullscreen && (
                <div style={{ position: 'absolute', top: 10, left: 10, right: 10, zIndex: 1100, display: 'flex', justifyContent: 'space-between', pointerEvents: 'none' }}>
                  <span style={{ pointerEvents: 'auto', background: 'rgba(255,255,255,0.92)', borderRadius: 4, padding: '2px 4px' }}>
                    <Button.Group size="small">
                      <Button icon="fast-backward" onClick={pb.reset} disabled={playbackPoints.length === 0}>Reset</Button>
                      <Button icon="step-backward" onClick={pb.back} disabled={pb.index <= 0}>Back</Button>
                      <Button type={pb.isPlaying ? 'default' : 'primary'} icon={pb.isPlaying ? 'pause' : 'caret-right'} onClick={pb.toggle} disabled={playbackPoints.length <= 1}>{pb.isPlaying ? 'Pause' : 'Play'}</Button>
                      <Button icon="step-forward" onClick={pb.step} disabled={pb.index >= playbackPoints.length - 1}>Step</Button>
                    </Button.Group>
                    {activePoint && <span style={{ marginLeft: 8, fontSize: 12 }}>{formatTime(activePoint.ts)}</span>}
                  </span>
                  <Button style={{ pointerEvents: 'auto' }} size="small" icon="fullscreen-exit" onClick={() => setIsFullscreen(false)}>Exit</Button>
                </div>
              )}
              <LeafletMap ref={mapRef} center={detectionCircles[0] ? [detectionCircles[0].lat, detectionCircles[0].lng] : [28.5, 77.1]} zoom={15} maxZoom={20} style={{ height: '100%', width: '100%' }}>
                <TileLayer attribution={ESRI_TILE_ATTRIBUTION} url={ESRI_TILE_URL} maxZoom={20} />

                {/* Organisation geo-fences (points of interest). */}
                {renderGeoFencePolygons(geoFences)}

                {/* Reader movement during each FOUND→LOST window. */}
                {intervalTracks.map((it, i) => (it.points.length >= 2 ? (
                  <Polyline key={`it-${i}`} positions={it.points.map((p) => [p.lat, p.lng])} color={TRACK_COLORS[i % TRACK_COLORS.length]} weight={3} opacity={0.65} />
                ) : null))}

                {/* Played-so-far overlay (dotted when hopping between detections,
                    since there is no real reader path). */}
                {pb.index > 0 && playedPositions.length >= 2 && (
                  <Polyline positions={playedPositions} color="#722ed1" weight={usingDetectionTimeline ? 2 : 4} opacity={0.85} dashArray={usingDetectionTimeline ? '2,8' : null} />
                )}

                {/* Signal-range circle per detection. */}
                {visibleCircles.map((c, i) => {
                  const color = CONFIDENCE_COLORS[c.confidence] || '#1890ff';
                  return (
                    <Circle key={`c-${c.id || i}`} center={[c.lat, c.lng]} radius={c.distance} color={color} fillColor={color} fillOpacity={0.1} weight={1} opacity={0.5}>
                      <LeafletTooltip direction="top">
                        <span style={{ fontSize: 11 }}>
                          {c.status === 'FOUND' ? 'FOUND' : c.status === 'LOST' ? 'LOST' : c.status} · {formatHm(c.ts)}<br />
                          BLE {assetLabel(queriedMac, assetInfo)}<br />
                          Reader {assetLabel(c.readerImei, resolveReader(c.readerImei))}<br />
                          {c.rssi} dBm · ~{c.distance.toFixed(0)} m
                        </span>
                      </LeafletTooltip>
                    </Circle>
                  );
                })}

                {/* FOUND (green) / LOST (red) event dots. */}
                {visibleDots.map((c, i) => (
                  <CircleMarker key={`e-${c.id || i}`} center={[c.lat, c.lng]} radius={focusedDetId ? 5 : 3} weight={1}
                    color={c.status === 'FOUND' ? '#52c41a' : c.status === 'LOST' ? '#ff4d4f' : '#8c8c8c'}
                    fillColor={c.status === 'FOUND' ? '#52c41a' : c.status === 'LOST' ? '#ff4d4f' : '#8c8c8c'} fillOpacity={1}>
                    <LeafletTooltip direction="top">
                      <span style={{ fontSize: 11 }}>{c.status || 'detected'} · {formatHm(c.ts)} · Reader {assetLabel(c.readerImei, resolveReader(c.readerImei))}</span>
                    </LeafletTooltip>
                  </CircleMarker>
                ))}

                {/* Playback marker — on reader movement, or hopping detections. */}
                {activePoint && (
                  <Marker position={[activePoint.lat, activePoint.lng]} icon={activePoint.noGps ? noGpsPinIcon : playbackPinIcon}>
                    <LeafletTooltip permanent direction="top" offset={[0, -22]} opacity={0.95}>
                      <span style={{ fontSize: 11 }}>
                        {activePoint.detection ? (
                          <>{activePoint.status || 'Detected'} · {formatHm(activePoint.ts)}<br />Reader {assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}{activePoint.rssi != null ? ` · ${activePoint.rssi} dBm` : ''}</>
                        ) : (
                          <>Reader {assetLabel(activePoint.readerImei, resolveReader(activePoint.readerImei))}<br />{formatHm(activePoint.ts)}{activePoint.speed != null ? ` · ${Number(activePoint.speed).toFixed(0)} km/h` : ''}</>
                        )}
                      </span>
                    </LeafletTooltip>
                  </Marker>
                )}
              </LeafletMap>
            </div>
          )}
        </Spin>
      </Card>

      <Card size="small" title={<span><Icon type="profile" /> BLE Events ({eventRows.length})</span>}>
        {eventRows.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No events loaded." />
        ) : (
          <div ref={tableBodyRef}>
            <Table
              rowKey={(r, i) => `evt-${i}`}
              size="small"
              columns={eventColumns}
              dataSource={eventRows}
              pagination={{ pageSize: 25, showSizeChanger: true }}
              scroll={{ x: 1000 }}
              onRow={(record) => ({
                onClick: () => { setFocusedDetId(`${record.eventTime}|${record.readerImei}`); seekToTs(record.eventTime); },
                style: focusedDetId === `${record.eventTime}|${record.readerImei}`
                  ? { background: '#e6f7ff', cursor: 'pointer' }
                  : { cursor: 'pointer' }
              })}
            />
          </div>
        )}
      </Card>
    </div>
  );
}

export default BleAssetTracker;
